import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiUser, FiMail, FiBriefcase, FiShield, FiDownloadCloud } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import MonthlyReportExport from '../components/MonthlyReportExport';
import api from '../services/api';

const defaultFlowSettings = {
  welcome_message:
    'Welcome to {resort_name}, {guest_name}! 🎉\n\nWe are delighted to have you with us. {room_text}\n\nI am your virtual concierge, here to assist you 24/7.',
  welcome_template_image_url: '',
  welcome_pdf_url: '',
  welcome_pdf_filename: 'welcome_guide.pdf',
  welcome_pdf_caption: 'Welcome to our resort! Here is your resort guide.',
  welcome_pdf_template_name: 'welcome_brochure_pdf',
  main_menu_message: 'Welcome! How can I assist you today?',
  main_menu_rows: [
    { id: 'menu_room_cleaning', title: 'Housekeeping Service', description: 'Request housekeeping help for your room', enabled: true },
    { id: 'menu_toiletries', title: 'Maintenance Assistance', description: 'Report maintenance issues and request assistance', enabled: true },
    { id: 'menu_help', title: 'IT Support', description: 'Get support for Wi-Fi, TV, and in-room tech', enabled: true },
    { id: 'menu_contact_concierge', title: 'Front Office', description: 'Connect with front office and concierge', enabled: true },
    { id: 'menu_wellness', title: 'Wellness', description: 'Book Spa, Yoga & Salon appointments', enabled: true },
    { id: 'menu_recreation', title: 'Recreation Activities', description: 'View recreation activities and guidelines', enabled: true },
    { id: 'menu_restaurants', title: 'Restaurant Table Booking', description: 'View and book restaurant tables', enabled: true },
    { id: 'menu_transport', title: 'Transport Arrangements', description: 'Arrange cabs, airport transfers & more', enabled: true },
    { id: 'menu_medical_emergency', title: '🚨 Medical Emergency', description: 'Get immediate medical assistance', enabled: true },
    { id: 'menu_security_emergency', title: '🛡️ Security Emergency', description: 'Get immediate security assistance', enabled: true }
  ],
  wellness_message: 'Please select a wellness service:',
  wellness_rows: [
    { id: 'wellness_spa', title: '💆 Spa', description: 'Book a spa appointment' },
    { id: 'wellness_yoga', title: '🧘 Yoga', description: 'Book yoga and wellness sessions' },
    { id: 'wellness_salon', title: '✂️ Salon', description: 'Book a salon appointment' }
  ],
  recreation_message: '*Recreation Activities & Sports Booking*\n\nPlease find our recreation brochure above. Call or message the number below to book your padel, pickleball, trampoline or recreation slots.',
  recreation_rows: [
    { id: 'recreation_padel', title: 'Padel', description: 'Reserve a padel court' },
    { id: 'recreation_pickleball', title: 'Pickleball', description: 'Reserve a pickleball court' },
    { id: 'recreation_trampoline', title: 'Lets Play Trampoline', description: 'Reserve a trampoline slot' }
  ],
  restaurants_message: '*Restaurant Table Booking*\n\nOur restaurants are available for table bookings.\nFor reservations, please call or message the number below.',
  restaurants: [
    { id: 'restaurant_1', name: 'Restaurant 1', phone: '', description: 'Fine dining experience' },
    { id: 'restaurant_2', name: 'Restaurant 2', phone: '', description: 'Casual dining' },
    { id: 'restaurant_3', name: 'Restaurant 3', phone: '', description: 'All-day dining' }
  ],
  restaurants_contact_name: 'Restaurant Reservations',
  restaurants_contact_phone: '',
  food_menu_pdf_url: '',
  food_menu_pdf_filename: 'food_menu.pdf',
  food_menu_pdf_caption: '*Food & Beverage Menu*\n\nPlease review the menu. You can type your order here.',
  activity_pdf_overrides: {
    Spa: { pdf_url: '', filename: 'spa_menu.pdf', caption: 'Spa Services Menu' },
    Yoga: { pdf_url: '', filename: 'yoga_info.pdf', caption: 'Yoga information and guidelines' },
    Salon: { pdf_url: '', filename: 'salon_menu.pdf', caption: 'Salon Services Menu' },
    Recreation: { pdf_url: '', filename: 'recreation_info.pdf', caption: 'Recreation information and guidelines' }
  },
  front_office_contact_name: 'Front Office',
  front_office_contact_phone: '',
  recreation_contact_name: 'Recreation Desk',
  recreation_contact_phone: '',
  medical_emergency_contact_name: 'Front Office Emergency',
  medical_emergency_contact_phone: '',
  security_emergency_contact_name: 'Security Desk',
  security_emergency_contact_phone: '',
  housekeeping_request_rows: [],
  it_support_request_rows: [],
  security_request_rows: [],
  maintenance_request_rows: [],
  front_office_request_rows: [],
  dispatch_department_defaults: {},
  staff_shift_presets: [
    { key: 'morning', name: 'Morning Shift', start_time: '07:00', end_time: '15:00' },
    { key: 'evening', name: 'Evening Shift', start_time: '15:00', end_time: '23:00' },
    { key: 'night', name: 'Night Shift', start_time: '23:00', end_time: '07:00' }
  ],
  room_presets: [],
  staff_checkin_reminder_time_ist: '09:30',
  staff_checkin_reminder_recipients: 'staff_and_hod',
  staff_checkin_template_name: '',
  staff_checkin_template_language: 'en_US',
  dob_required_for_guest_creation: true,
  toiletries_prompt_message:
    '*Toiletries & Supplies Request*\n\nFormat: *Quantity(1,2,3,etc) - Product(Towel, Brush, Pillow, etc.)*\n\nExamples:\n• 3-Towels\n• 2-Pillows, 1-Brush',
  cutlery_prompt_message:
    '*Cutlery Request*\n\nPlease write the cutlery you need.\n\nExamples:\n• 2 spoon\n• 1 plate\n• 2 spoon, 1 plate',
  completion_photo_required_activities: [],
  emergency_contact_phone: '',
  transport_brochure_pdf_url: '',
  transport_brochure_pdf_caption: '*🚗 Transport Arrangements*\n\nPlease find our transport services brochure below. We offer cabs, airport transfers, and more.',
  transport_contact_name: 'Transport Desk',
  transport_contact_phone: ''
};

const ACTIVITY_PDF_OVERRIDE_KEYS = ['Spa', 'Yoga', 'Salon', 'Recreation'];

const STAFF_DISPATCH_ACTIVITIES = [
  'Housekeeping Service',
  'Maintenance Assistance',
  'IT Support',
  'Front Office',
  'Medical Emergency',
  'Security Assistance',
  'SPA Appointment',
  'Yoga & Wellness',
];

function Settings() {
  const { user } = useAuth();
  const location = useLocation();
  const allowedTabs = Array.isArray(user?.allowed_tabs) ? user.allowed_tabs : [];
  const hasSettingsAccess = allowedTabs.length === 0 || allowedTabs.includes('/settings');
  const isDispatchOnlyView = location.pathname === '/settings/dispatch-routing';
  const isAttendanceOnlyView = location.pathname === '/settings/attendance';
  const canManageFlow = user?.role === 'admin' || user?.role === 'manager';
  const canManageRouting = canManageFlow || user?.role === 'department';

  const displayActivities = useMemo(() => {
    if (user?.role === 'department' && user?.department) {
      return STAFF_DISPATCH_ACTIVITIES.filter(d => d.toLowerCase() === user.department.toLowerCase());
    }
    return STAFF_DISPATCH_ACTIVITIES;
  }, [user]);

  const [flowSettings, setFlowSettings] = useState(defaultFlowSettings);
  const [loadingFlow, setLoadingFlow] = useState(canManageFlow);
  const [savingFlow, setSavingFlow] = useState(false);
  const [flowMessage, setFlowMessage] = useState('');
  const [routingRules, setRoutingRules] = useState([]);
  const SLOT_SERVICES = ['Spa', 'Yoga', 'Salon'];
  const [slotCapacities, setSlotCapacities] = useState({});
  const [routingMessage, setRoutingMessage] = useState('');
  const [loadingRouting, setLoadingRouting] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [attendanceDepartmentFilter, setAttendanceDepartmentFilter] = useState('all');
  const [attendanceStatusFilter, setAttendanceStatusFilter] = useState('all');
  const [attendanceSearch, setAttendanceSearch] = useState('');
  const [departmentStaffDirectory, setDepartmentStaffDirectory] = useState([]);
  const [newRule, setNewRule] = useState({
    department: displayActivities[0] || STAFF_DISPATCH_ACTIVITIES[0],
    room_match_type: 'range',
    room_start: '1',
    room_end: '9999',
    room_custom_values: '',
    primary_staff_ids: [],
    hod_staff_ids: [],
    super_hod_staff_ids: [],
    escalation_minutes: 10,
    completion_followup_minutes: 10,
    super_hod_escalation_minutes: 0,
    is_active: true
  });
  const [settingsTab, setSettingsTab] = useState(
    isDispatchOnlyView ? 'dispatch' : isAttendanceOnlyView ? 'attendance' : 'general'
  );
  const [linkCopied, setLinkCopied] = useState(false);
  const [activeFlowSection, setActiveFlowSection] = useState('common');
  const routingFormRef = useRef(null);

  const guestFormUrl = `${window.location.origin}/guest-registration`;
  const guestFormQrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(guestFormUrl)}`;

  const renderWelcomePreview = (template) => {
    const source = String(template || '');
    return source
      .replace(/\{guest_name\}/gi, 'Priyansh Patel')
      .replace(/\{resort_name\}/gi, 'Oleanders Farms')
      .replace(/\{room_no\}/gi, '309')
      .replace(/\{room_text\}/gi, 'Your room 309 is ready for your stay.');
  };

  useEffect(() => {
    if (canManageFlow) {
      fetchFlowSettings();
      fetchRoutingRules();
      fetchDepartmentStaffDirectory();
      fetchAttendance(new Date().toISOString().split('T')[0]);
      fetchSlotCapacities();
    }
  }, [canManageFlow]);

  const fetchSlotCapacities = async () => {
    try {
      const capacities = {};
      for (const service of SLOT_SERVICES) {
        try {
          const response = await api.get(`/api/slots/${service}`);
          capacities[service] = response.data?.workingHours?.capacity || 1;
        } catch (e) {
          capacities[service] = 1;
        }
      }
      setSlotCapacities(capacities);
    } catch (error) {
      console.error('Failed to fetch slot capacities', error);
    }
  };

  const fetchRoutingRules = async () => {
    setLoadingRouting(true);
    try {
      const response = await api.get('/api/admin/staff-routing-rules');
      setRoutingRules(response.data || []);
    } catch (error) {
      console.error('Failed to fetch staff routing rules:', error);
      setRoutingMessage('Failed to load staff routing rules');
    } finally {
      setLoadingRouting(false);
    }
  };

  const fetchAttendance = async (date) => {
    try {
      const response = await api.get('/api/admin/staff-attendance', { params: { date } });
      setAttendanceRows(response.data || []);
    } catch (error) {
      console.error('Failed to fetch staff attendance:', error);
      setRoutingMessage('Failed to load staff attendance');
    }
  };

  const fetchDepartmentStaffDirectory = async () => {
    try {
      const response = await api.get('/api/admin/staff?is_active=true');
      setDepartmentStaffDirectory(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch department staff directory:', error);
      setDepartmentStaffDirectory([]);
    }
  };

  const normalizePhoneForMatch = (phone) => {
    const digits = String(phone || '').replace(/\D/g, '');
    return digits.length > 10 ? digits.slice(-10) : digits;
  };

  const formatAttendanceDateTime = (value) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleString();
  };

  const getAttendanceDuration = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return '—';
    const start = new Date(checkIn).getTime();
    const end = new Date(checkOut).getTime();
    if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return '—';

    const diffMinutes = Math.round((end - start) / 60000);
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  };

  const getAttendanceStatus = (row) => {
    if (row?.attendance_status) return row.attendance_status;
    if (row?.checked_out_at) return 'checked_out';
    if (row?.checked_in_at) return 'on_duty';
    return 'absent';
  };

  const attendanceInsights = useMemo(() => {
    const attendanceByPhone = new Map();
    attendanceRows.forEach((row) => {
      const key = normalizePhoneForMatch(row.phone);
      if (!key) return;
      attendanceByPhone.set(key, row);
    });

    const mappedDirectoryRows = (departmentStaffDirectory || []).map((staff) => {
      const key = normalizePhoneForMatch(staff.phone);
      const attendanceRow = key ? attendanceByPhone.get(key) : null;

      if (attendanceRow) {
        return {
          ...attendanceRow,
          staff_name: attendanceRow.staff_name || staff.name,
          staff_department: attendanceRow.staff_department || staff.department,
          phone: attendanceRow.phone || staff.phone,
          attendance_status: getAttendanceStatus(attendanceRow)
        };
      }

      return {
        id: `absent-${staff.id}`,
        staff_name: staff.name,
        staff_department: staff.department,
        phone: staff.phone,
        checked_in_at: null,
        checked_out_at: null,
        last_event_type: null,
        last_message: null,
        attendance_status: 'absent'
      };
    });

    const knownPhones = new Set((departmentStaffDirectory || []).map((staff) => normalizePhoneForMatch(staff.phone)));
    const unmatchedRows = (attendanceRows || [])
      .filter((row) => !knownPhones.has(normalizePhoneForMatch(row.phone)))
      .map((row) => ({
        ...row,
        attendance_status: getAttendanceStatus(row)
      }));

    const records = [...mappedDirectoryRows, ...unmatchedRows].sort((a, b) => {
      const aName = String(a.staff_name || '').toLowerCase();
      const bName = String(b.staff_name || '').toLowerCase();
      if (aName && bName) return aName.localeCompare(bName);
      return String(a.phone || '').localeCompare(String(b.phone || ''));
    });

    const totals = {
      total_staff: records.length,
      present: records.filter((row) => ['on_duty', 'checked_out'].includes(getAttendanceStatus(row))).length,
      on_duty: records.filter((row) => getAttendanceStatus(row) === 'on_duty').length,
      checked_out: records.filter((row) => getAttendanceStatus(row) === 'checked_out').length,
      absent: records.filter((row) => getAttendanceStatus(row) === 'absent').length
    };

    return { records, totals };
  }, [attendanceRows, departmentStaffDirectory]);

  const attendanceDepartmentOptions = useMemo(() => {
    const values = new Set();
    attendanceInsights.records.forEach((row) => {
      if (row.staff_department) values.add(row.staff_department);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [attendanceInsights]);

  const filteredAttendanceRecords = useMemo(() => {
    const search = attendanceSearch.trim().toLowerCase();

    return attendanceInsights.records.filter((row) => {
      const status = getAttendanceStatus(row);

      if (attendanceDepartmentFilter !== 'all' && row.staff_department !== attendanceDepartmentFilter) {
        return false;
      }

      if (attendanceStatusFilter !== 'all' && status !== attendanceStatusFilter) {
        return false;
      }

      if (!search) return true;

      const haystack = [
        row.staff_name,
        row.staff_department,
        row.phone
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(search);
    });
  }, [attendanceInsights, attendanceDepartmentFilter, attendanceStatusFilter, attendanceSearch]);

  const findStaffIdByPhone = (phone, isHod) => {
    const target = normalizePhoneForMatch(phone);
    if (!target) return '';

    const match = departmentStaffDirectory.find((staff) => {
      const staffPhone = normalizePhoneForMatch(staff.phone);
      if (!staffPhone || staffPhone !== target) return false;
      return isHod ? !!staff.is_hod : !staff.is_hod;
    });

    return match?.id || '';
  };

  const findStaffIdsByPhones = (phones = []) => {
    const targets = Array.from(
      new Set((Array.isArray(phones) ? phones : [phones]).map((phone) => normalizePhoneForMatch(phone)).filter(Boolean))
    );

    if (targets.length === 0) return [];

    return departmentStaffDirectory
      .filter((staff) => {
        const staffPhone = normalizePhoneForMatch(staff.phone);
        return staffPhone && targets.includes(staffPhone) && !staff.is_hod;
      })
      .map((staff) => staff.id);
  };

  const startEditRule = (rule) => {
    setEditingRuleId(rule.id);
    const existingPrimaryPhones = Array.isArray(rule.primary_phones) && rule.primary_phones.length > 0
      ? rule.primary_phones
      : [rule.primary_phone].filter(Boolean);
    setNewRule({
      department: rule.department,
      room_match_type: rule.room_match_type || 'range',
      room_start: rule.room_start,
      room_end: rule.room_end,
      room_custom_values: rule.room_custom_values || '',
      primary_staff_ids: findStaffIdsByPhones(existingPrimaryPhones),
      hod_staff_ids: findHodStaffIdsByPhones(
        Array.isArray(rule.escalation_phones) && rule.escalation_phones.length > 0
          ? rule.escalation_phones
          : (rule.escalation_phone ? [rule.escalation_phone] : [])
      ),
      super_hod_staff_ids: findHodStaffIdsByPhones(
        Array.isArray(rule.super_hod_phones) && rule.super_hod_phones.length > 0
          ? rule.super_hod_phones
          : (rule.super_hod_phone ? [rule.super_hod_phone] : [])
      ),
      escalation_minutes: rule.escalation_minutes || 10,
      completion_followup_minutes: rule.completion_followup_minutes || 10,
      super_hod_escalation_minutes: rule.super_hod_escalation_minutes || 0,
      is_active: rule.is_active
    });

    setRoutingMessage(`Editing rule for ${rule.department} (${getRoomMatchLabel(rule)})`);
    setTimeout(() => {
      routingFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const cancelEditRule = () => {
    setEditingRuleId(null);
    setNewRule({
      department: STAFF_DISPATCH_ACTIVITIES[0],
      room_match_type: 'range',
      room_start: '1',
      room_end: '9999',
      room_custom_values: '',
      primary_staff_ids: [],
      hod_staff_ids: [],
      super_hod_staff_ids: [],
      escalation_minutes: 10,
      completion_followup_minutes: 10,
      super_hod_escalation_minutes: 0,
      is_active: true
    });
    setRoutingMessage('Edit cancelled');
  };

  const togglePrimaryStaffSelection = (staffId) => {
    setNewRule((prev) => {
      const selected = Array.isArray(prev.primary_staff_ids) ? prev.primary_staff_ids : [];
      const exists = selected.includes(staffId);
      return {
        ...prev,
        primary_staff_ids: exists
          ? selected.filter((id) => id !== staffId)
          : [...selected, staffId]
      };
    });
  };

  const toggleHodStaffSelection = (staffId) => {
    setNewRule((prev) => {
      const selected = Array.isArray(prev.hod_staff_ids) ? prev.hod_staff_ids : [];
      const exists = selected.includes(staffId);
      return {
        ...prev,
        hod_staff_ids: exists
          ? selected.filter((id) => id !== staffId)
          : [...selected, staffId]
      };
    });
  };

  const findHodStaffIdsByPhones = (phones = []) => {
    const targets = Array.from(
      new Set((Array.isArray(phones) ? phones : [phones]).map((phone) => normalizePhoneForMatch(phone)).filter(Boolean))
    );
    if (targets.length === 0) return [];
    return departmentStaffDirectory
      .filter((staff) => {
        const staffPhone = normalizePhoneForMatch(staff.phone);
        return staffPhone && targets.includes(staffPhone) && !!staff.is_hod;
      })
      .map((staff) => staff.id);
  };

  const toggleSuperHodStaffSelection = (staffId) => {
    setNewRule((prev) => {
      const selected = Array.isArray(prev.super_hod_staff_ids) ? prev.super_hod_staff_ids : [];
      const exists = selected.includes(staffId);
      return {
        ...prev,
        super_hod_staff_ids: exists
          ? selected.filter((id) => id !== staffId)
          : [...selected, staffId]
      };
    });
  };

  const createRoutingRule = async (e) => {
    e.preventDefault();
    setRoutingMessage('');

    const escalationMinutes = parseInt(newRule.escalation_minutes, 10);
    if (Number.isNaN(escalationMinutes) || escalationMinutes < 1) {
      setRoutingMessage('Escalation minutes must be at least 1');
      return;
    }

    const completionFollowupMinutes = parseInt(newRule.completion_followup_minutes, 10);
    if (Number.isNaN(completionFollowupMinutes) || completionFollowupMinutes < 1) {
      setRoutingMessage('Completion follow-up minutes must be at least 1');
      return;
    }

    const selectedPrimaryStaffList = departmentStaffDirectory.filter(
      (staff) => Array.isArray(newRule.primary_staff_ids) && newRule.primary_staff_ids.includes(staff.id)
    );

    const selectedPrimaryPhones = Array.from(
      new Set(
        selectedPrimaryStaffList
          .map((staff) => String(staff.phone || '').trim())
          .filter(Boolean)
      )
    );

    if (selectedPrimaryPhones.length === 0) {
      setRoutingMessage('Please select at least one employee with a valid WhatsApp number');
      return;
    }

    const selectedHodStaffList = departmentStaffDirectory.filter(
      (staff) => Array.isArray(newRule.hod_staff_ids) && newRule.hod_staff_ids.includes(staff.id)
    );
    const selectedEscalationPhones = Array.from(
      new Set(selectedHodStaffList.map((staff) => String(staff.phone || '').trim()).filter(Boolean))
    );

    const selectedSuperHodStaffList = departmentStaffDirectory.filter(
      (staff) => Array.isArray(newRule.super_hod_staff_ids) && newRule.super_hod_staff_ids.includes(staff.id)
    );
    const selectedSuperHodPhones = Array.from(
      new Set(selectedSuperHodStaffList.map((staff) => String(staff.phone || '').trim()).filter(Boolean))
    );
    const parsedSuperHodMinutes = parseInt(newRule.super_hod_escalation_minutes, 10) || 0;

    if (newRule.room_match_type === 'custom') {
      if (!String(newRule.room_custom_values || '').trim()) {
        setRoutingMessage('Please enter custom rooms (comma-separated)');
        return;
      }
    } else {
      const start = parseInt(newRule.room_start, 10);
      const end = parseInt(newRule.room_end, 10);
      if (Number.isNaN(start) || Number.isNaN(end)) {
        setRoutingMessage('Room start and room end must be valid numbers');
        return;
      }
      if (start > end) {
        setRoutingMessage('Room start must be less than or equal to room end');
        return;
      }
    }

    try {
      const payload = {
        department: newRule.department,
        room_match_type: newRule.room_match_type,
        room_start: newRule.room_start,
        room_end: newRule.room_end,
        room_custom_values: newRule.room_custom_values,
        primary_phones: selectedPrimaryPhones,
        primary_phone: selectedPrimaryPhones[0],
        escalation_phones: selectedEscalationPhones,
        escalation_phone: selectedEscalationPhones[0] || null,
        escalation_minutes: escalationMinutes,
        completion_followup_minutes: completionFollowupMinutes,
        super_hod_phones: selectedSuperHodPhones,
        super_hod_phone: selectedSuperHodPhones[0] || null,
        super_hod_escalation_minutes: parsedSuperHodMinutes
      };

      if (newRule.room_match_type === 'custom') {
        payload.room_custom_values = String(newRule.room_custom_values || '').trim();
        payload.room_start = 0;
        payload.room_end = 0;
      } else {
        payload.room_match_type = 'range';
        payload.room_start = parseInt(newRule.room_start, 10);
        payload.room_end = parseInt(newRule.room_end, 10);
        payload.room_custom_values = null;
      }

      if (editingRuleId) {
        await api.put(`/api/admin/staff-routing-rules/${editingRuleId}`, payload);
      } else {
        await api.post('/api/admin/staff-routing-rules', payload);
      }

      setNewRule({
        department: displayActivities[0] || STAFF_DISPATCH_ACTIVITIES[0],
        room_match_type: 'range',
        room_start: '1',
        room_end: '9999',
        room_custom_values: '',
        primary_staff_ids: [],
        hod_staff_ids: [],
        super_hod_staff_ids: [],
        escalation_minutes: 10,
        completion_followup_minutes: 10,
        super_hod_escalation_minutes: 0,
        is_active: true
      });
      setEditingRuleId(null);
      setRoutingMessage(editingRuleId ? 'Routing rule updated successfully' : 'Routing rule created successfully');
      fetchRoutingRules();
    } catch (error) {
      console.error('Failed to save routing rule:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to save routing rule';
      setRoutingMessage(errorMessage);
    }
  };

  const getRoomMatchLabel = (rule) => {
    if (rule.room_match_type === 'custom') {
      return rule.room_custom_values || 'Custom';
    }
    return `${rule.room_start} - ${rule.room_end}`;
  };

  const getEmployeeOptions = (department) => {
    return departmentStaffDirectory.filter(
      (staff) => staff.department === department && !staff.is_hod && staff.is_active
    );
  };

  const getHodOptions = (department) => {
    return departmentStaffDirectory.filter(
      (staff) => staff.department === department && !!staff.is_hod && staff.is_active
    );
  };

  const toggleRoutingRule = async (rule) => {
    try {
      await api.put(`/api/admin/staff-routing-rules/${rule.id}`, {
        is_active: !rule.is_active
      });
      fetchRoutingRules();
    } catch (error) {
      console.error('Failed to toggle routing rule:', error);
      setRoutingMessage('Failed to update routing rule');
    }
  };

  const deleteRoutingRule = async (ruleId) => {
    try {
      await api.delete(`/api/admin/staff-routing-rules/${ruleId}`);
      fetchRoutingRules();
    } catch (error) {
      console.error('Failed to delete routing rule:', error);
      setRoutingMessage('Failed to delete routing rule');
    }
  };

  const runEscalationCheck = async () => {
    try {
      await api.post('/api/admin/staff-dispatch/process-escalations');
      setRoutingMessage('Escalation check completed');
    } catch (error) {
      console.error('Failed to process escalations:', error);
      setRoutingMessage('Failed to process escalations');
    }
  };


  const fetchFlowSettings = async () => {
    setLoadingFlow(true);
    try {
      const response = await api.get('/api/admin/whatsapp-flow-settings');
      setFlowSettings({ ...defaultFlowSettings, ...(response.data || {}) });
    } catch (error) {
      console.error('Failed to fetch WhatsApp flow settings:', error);
      setFlowMessage('Failed to load WhatsApp flow settings');
    } finally {
      setLoadingFlow(false);
    }
  };

  const updateRowField = (groupKey, rowId, field, value) => {
    setFlowSettings((prev) => ({
      ...prev,
      [groupKey]: (prev[groupKey] || []).map((row) =>
        row.id === rowId ? { ...row, [field]: value } : row
      )
    }));
  };


  const updateRequestOptionField = (groupKey, rowId, field, value) => {
    setFlowSettings((prev) => ({
      ...prev,
      [groupKey]: (prev[groupKey] || []).map((row) =>
        row.id === rowId ? { ...row, [field]: value } : row
      )
    }));
  };

  const addRequestOptionRow = (groupKey, prefix, label) => {
    const newRow = {
      id: `${prefix}_${Date.now()}`,
      title: `${label} ${((flowSettings[groupKey] || []).length + 1)}`.slice(0, 20),
      description: `Description for ${label}`,
      detail_prompt_enabled: false,
      detail_prompt_message: '',
      escalation_minutes: 0
    };
    setFlowSettings((prev) => {
      const rows = prev[groupKey] || [];
      const othersIndex = rows.findIndex(r => String(r.title || '').trim().toLowerCase() === 'others');
      let newRows;
      if (othersIndex !== -1) {
        newRows = [...rows];
        newRows.splice(othersIndex, 0, newRow);
      } else {
        newRows = [...rows, newRow];
      }
      return { ...prev, [groupKey]: newRows };
    });
  };

  const getSortedOptions = (options) => {
    if (!Array.isArray(options)) return [];
    const others = options.filter(r => String(r.title || '').trim().toLowerCase() === 'others');
    const nonOthers = options.filter(r => String(r.title || '').trim().toLowerCase() !== 'others');
    return [...nonOthers, ...others];
  };

  const removeRequestOptionRow = (groupKey, rowId) => {
    setFlowSettings((prev) => ({
      ...prev,
      [groupKey]: (prev[groupKey] || []).filter((row) => row.id !== rowId)
    }));
  };


  const updateActivityPdfOverride = (activityKey, field, value) => {
    setFlowSettings((prev) => ({
      ...prev,
      activity_pdf_overrides: {
        ...(prev.activity_pdf_overrides || {}),
        [activityKey]: {
          ...((prev.activity_pdf_overrides || {})[activityKey] || {}),
          [field]: value
        }
      }
    }));
  };

  const saveFlowSettings = async (e) => {
    e.preventDefault();
    setSavingFlow(true);
    setFlowMessage('');
    try {
      const sortedSettings = {
        ...flowSettings,
        housekeeping_request_rows: getSortedOptions(flowSettings.housekeeping_request_rows),
        it_support_request_rows: getSortedOptions(flowSettings.it_support_request_rows),
        maintenance_request_rows: getSortedOptions(flowSettings.maintenance_request_rows),
        front_office_request_rows: getSortedOptions(flowSettings.front_office_request_rows),
        security_request_rows: getSortedOptions(flowSettings.security_request_rows)
      };
      const response = await api.put('/api/admin/whatsapp-flow-settings', sortedSettings);
      setFlowSettings(response.data || sortedSettings);

      // Save slot capacities
      for (const service of SLOT_SERVICES) {
        if (slotCapacities[service]) {
          try {
            const slotRes = await api.get(`/api/slots/${service}`);
            const existing = slotRes.data?.workingHours || { start_time: '07:00', end_time: '21:00', duration_minutes: 60 };
            await api.put(`/api/slots/${service}/working-hours`, {
              ...existing,
              capacity: parseInt(slotCapacities[service], 10) || 1
            });
          } catch (e) {
            console.error(`Failed to save capacity for ${service}`, e);
          }
        }
      }

      setFlowMessage('WhatsApp flow settings and capacities saved successfully');
      setTimeout(() => setFlowMessage(''), 3000);
    } catch (error) {
      console.error('Failed to save WhatsApp flow settings:', error);
      setFlowMessage('Failed to save WhatsApp flow settings');
    } finally {
      setSavingFlow(false);
    }
  };

  const copyGuestFormLink = async () => {
    try {
      await navigator.clipboard.writeText(guestFormUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1500);
    } catch (error) {
      setFlowMessage('Unable to copy link. Please copy manually.');
    }
  };

  if (!hasSettingsAccess) {
    return (
      <Layout>
        <div className="p-8 text-center bg-white rounded-xl border border-gray-200 shadow-sm mt-4">
          <h2 className="text-lg font-semibold text-gray-900">Access Restricted</h2>
          <p className="text-sm text-gray-500 mt-2">You do not have permission to view or manage Settings.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5 px-2 py-3 sm:px-3 sm:py-4 lg:px-1 lg:py-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{isDispatchOnlyView ? 'Dispatch Routing' : 'Settings'}</h1>
          <p className="text-sm text-gray-600 mt-1">
            {isDispatchOnlyView
              ? 'Configure routing rules only. Live dispatch progress is visible in Service Requests.'
              : 'Manage your profile and account preferences.'}
          </p>
        </div>

        {canManageFlow && (
          <div className="overflow-x-auto pb-1">
            <div className="flex items-center gap-1.5 min-w-max rounded-xl bg-gray-50 border border-gray-200 p-1.5">
              <button
                type="button"
                onClick={() => setSettingsTab('general')}
                className={`px-3.5 py-2 rounded-lg font-medium text-sm transition-all whitespace-nowrap cursor-pointer ${
                  settingsTab === 'general'
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-200 font-bold'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/60'
                }`}
              >
                General Settings
              </button>

              <button
                type="button"
                onClick={() => setSettingsTab('reports')}
                className={`px-3.5 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                  settingsTab === 'reports'
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-200 font-bold'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/60'
                }`}
              >
                <FiDownloadCloud className={settingsTab === 'reports' ? 'text-indigo-600' : 'text-gray-400'} size={15} />
                <span>Monthly Reports (PDF)</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-extrabold border border-indigo-200">
                  New
                </span>
              </button>

              <button
                type="button"
                onClick={() => setSettingsTab('dispatch')}
                className={`px-3.5 py-2 rounded-lg font-medium text-sm transition-all whitespace-nowrap cursor-pointer ${
                  settingsTab === 'dispatch'
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-200 font-bold'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/60'
                }`}
              >
                Dispatch &amp; Routing
              </button>

              <button
                type="button"
                onClick={() => setSettingsTab('attendance')}
                className={`px-3.5 py-2 rounded-lg font-medium text-sm transition-all whitespace-nowrap cursor-pointer ${
                  settingsTab === 'attendance'
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-200 font-bold'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/60'
                }`}
              >
                Attendance Insights
              </button>
            </div>
          </div>
        )}

        {settingsTab === 'reports' && (
          <MonthlyReportExport />
        )}

        {settingsTab === 'general' && !isDispatchOnlyView && !isAttendanceOnlyView && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Profile Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <FiUser className="h-4 w-4 text-gray-500 mt-1" />
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Name</p>
                <p className="text-sm font-medium text-gray-900">{user?.name || 'Not available'}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <FiMail className="h-4 w-4 text-gray-500 mt-1" />
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Email</p>
                <p className="text-sm font-medium text-gray-900">{user?.email || 'Not available'}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <FiShield className="h-4 w-4 text-gray-500 mt-1" />
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Role</p>
                <p className="text-sm font-medium text-gray-900 capitalize">{user?.role || 'Not available'}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <FiBriefcase className="h-4 w-4 text-gray-500 mt-1" />
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Department</p>
                <p className="text-sm font-medium text-gray-900">{user?.department || 'Not assigned'}</p>
              </div>
            </div>
          </div>
        </div>
        )}

        {canManageFlow && settingsTab === 'general' && !isDispatchOnlyView && !isAttendanceOnlyView && (
          <form onSubmit={saveFlowSettings} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">WhatsApp Flow Settings</h2>
              <p className="text-sm text-gray-600 mt-1">Configure only key operational fields used in live WhatsApp flow.</p>
            </div>

            {flowMessage && (
              <div className={`rounded-lg px-3 py-2 text-sm ${flowMessage.includes('success') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {flowMessage}
              </div>
            )}

            {loadingFlow ? (
              <div className="text-sm text-gray-600">Loading flow settings...</div>
            ) : (
              <>
                <div className="-mx-1 overflow-x-auto pb-1">
                  <div className="flex flex-wrap gap-2 px-1 min-w-max">
                  {[
                    { id: 'common', label: 'Common' },
                    { id: 'recreation', label: '🎾 Recreation' },
                    { id: 'restaurants', label: '🍽️ Restaurants' },
                    { id: 'housekeeping', label: 'Housekeeping' },
                    { id: 'maintenance', label: 'Maintenance' },
                    { id: 'it_support', label: 'IT Support' },
                    { id: 'front_office', label: 'Front Office' },
                    { id: 'pdf_media', label: 'PDF / Media' },
                    { id: 'medical_emergency', label: 'Medical Emergency' },
                    { id: 'security', label: 'Security Assistance' },
                    { id: 'transport', label: '🚗 Transport' },
                    { id: 'slot_capacities', label: 'Slot Capacities' }
                  ].map((section) => (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => setActiveFlowSection(section.id)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                        activeFlowSection === section.id
                          ? 'bg-gray-900 text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      {section.label}
                    </button>
                  ))}
                  </div>
                </div>

                <div className="space-y-5">
                  {activeFlowSection === 'pdf_media' && (
                  <>
                  <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                    <h3 className="text-sm font-semibold text-gray-900">Guest Form & Operational Rules</h3>
                    <p className="text-xs text-gray-600 mt-1">Quick controls for guest onboarding and request-completion behavior.</p>

                    <div className="mt-4 border border-gray-200 rounded-xl p-4 bg-white">
                      <h4 className="text-sm font-semibold text-gray-900">Guest Self Check-in Form Link</h4>
                      <p className="text-xs text-gray-600 mt-1">Share this URL or QR with guests to fill their details directly from phone and auto-trigger welcome message.</p>
                      <div className="mt-3 flex flex-col md:flex-row md:items-center gap-4">
                        <div className="flex-1">
                          <input
                            type="text"
                            value={guestFormUrl}
                            readOnly
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm"
                          />
                          <button
                            type="button"
                            onClick={copyGuestFormLink}
                            className="mt-2 px-3 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-100"
                          >
                            {linkCopied ? 'Copied' : 'Copy Link'}
                          </button>
                        </div>
                        <div className="w-[180px] h-[180px] border border-gray-200 rounded-lg overflow-hidden bg-white flex items-center justify-center">
                          <img src={guestFormQrUrl} alt="Guest form QR" className="w-full h-full object-contain" />
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 border border-gray-200 rounded-xl p-4 bg-white">
                      <h4 className="text-sm font-semibold text-gray-900">Guest Form Rules</h4>
                      <p className="text-xs text-gray-600 mt-1">Control required fields while adding guests from dashboard and guest self check-in form.</p>
                      <label className="mt-3 inline-flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={flowSettings.dob_required_for_guest_creation !== false}
                          onChange={(e) =>
                            setFlowSettings((prev) => ({
                              ...prev,
                              dob_required_for_guest_creation: e.target.checked
                            }))
                          }
                          className="h-4 w-4 text-gray-900 border-gray-300 rounded"
                        />
                        Date of Birth is required when creating a guest
                      </label>
                    </div>

                    <div className="mt-4 border border-gray-200 rounded-xl p-4 bg-white">
                      <h4 className="text-sm font-semibold text-gray-900">Daily Check-in Reminder</h4>
                      <p className="text-xs text-gray-600 mt-1">
                        Send an automatic WhatsApp reminder at fixed IST time with a one-tap Check In action.
                      </p>

                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Daily Reminder Time (IST)</label>
                          <input
                            type="time"
                            value={flowSettings.staff_checkin_reminder_time_ist || '09:30'}
                            onChange={(e) =>
                              setFlowSettings((prev) => ({
                                ...prev,
                                staff_checkin_reminder_time_ist: e.target.value || '09:30'
                              }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Send To</label>
                          <select
                            value={flowSettings.staff_checkin_reminder_recipients || 'staff_and_hod'}
                            onChange={(e) =>
                              setFlowSettings((prev) => ({
                                ...prev,
                                staff_checkin_reminder_recipients: e.target.value
                              }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          >
                            <option value="staff_and_hod">Staff + HOD</option>
                            <option value="hod_only">HOD Only</option>
                          </select>
                        </div>
                      </div>

                    </div>

                    <div className="mt-4 border border-gray-200 rounded-xl p-4 bg-white">
                      <h4 className="text-sm font-semibold text-gray-900">Completion Photo Requirements</h4>
                      <p className="text-xs text-gray-600 mt-1">
                        Select which services require staff to share a photo proof on WhatsApp before the request is marked as complete.
                      </p>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {displayActivities.map((activity) => {
                          const checked = Array.isArray(flowSettings.completion_photo_required_activities)
                            ? flowSettings.completion_photo_required_activities.includes(activity)
                            : false;
                          return (
                            <label
                              key={activity}
                              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                                checked
                                  ? 'border-gray-900 bg-gray-900 text-white'
                                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  setFlowSettings((prev) => {
                                    const current = Array.isArray(prev.completion_photo_required_activities)
                                      ? prev.completion_photo_required_activities
                                      : [];
                                    return {
                                      ...prev,
                                      completion_photo_required_activities: e.target.checked
                                        ? [...current, activity]
                                        : current.filter((a) => a !== activity)
                                    };
                                  });
                                }}
                                className="h-4 w-4 rounded border-gray-300 accent-white"
                              />
                              <span className="text-sm font-medium">{activity}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                    <h3 className="text-sm font-semibold text-gray-900">WhatsApp Messages</h3>
                    <p className="text-xs text-gray-600 mt-1">Manage guest-facing message copy used in WhatsApp flows.</p>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Welcome Message (Used in WhatsApp Welcome)</label>
                      <textarea
                        value={flowSettings.welcome_message || ''}
                        onChange={(e) => setFlowSettings((prev) => ({ ...prev, welcome_message: e.target.value }))}
                        rows={5}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
                      />
                      <p className="text-xs text-gray-500 mt-1">Placeholders: {'{guest_name}'}, {'{resort_name}'}, {'{room_text}'}, {'{room_no}'}</p>

                      <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Preview</p>
                        <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans leading-relaxed">
                          {renderWelcomePreview(flowSettings.welcome_message)}
                        </pre>
                      </div>
                    </div>

                    <div className="mt-4 border border-gray-200 rounded-xl p-4 bg-white">
                      <h4 className="text-sm font-semibold text-gray-900">Welcome PDF (Brochure / Resort Guide)</h4>
                      <p className="text-xs text-gray-600 mt-1">
                        Automatically sent to the guest immediately after check-in / welcome message template.
                      </p>
                      <div className="mt-3 space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">PDF Direct Link / URL (e.g. Supabase Storage link)</label>
                          <input
                            type="url"
                            value={flowSettings.welcome_pdf_url || ''}
                            onChange={(e) => setFlowSettings((prev) => ({ ...prev, welcome_pdf_url: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            placeholder="https://...supabase.co/.../welcome_guide.pdf"
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">PDF Filename (shown in WhatsApp)</label>
                            <input
                              type="text"
                              value={flowSettings.welcome_pdf_filename || ''}
                              onChange={(e) => setFlowSettings((prev) => ({ ...prev, welcome_pdf_filename: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              placeholder="welcome_guide.pdf"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">WhatsApp Document Template Name (Optional)</label>
                            <input
                              type="text"
                              value={flowSettings.welcome_pdf_template_name || ''}
                              onChange={(e) => setFlowSettings((prev) => ({ ...prev, welcome_pdf_template_name: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              placeholder="welcome_brochure_pdf (or leave blank for standard doc)"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Main Menu Message</label>
                      <textarea
                        value={flowSettings.main_menu_message || ''}
                        onChange={(e) => setFlowSettings((prev) => ({ ...prev, main_menu_message: e.target.value }))}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
                      />
                    </div>

                    <div className="mt-4 space-y-3">
                      <h4 className="text-sm font-semibold text-gray-900">Main Menu Options</h4>
                      <p className="text-xs text-gray-600">
                        Edit titles here. Use <strong>Show in menu</strong> to hide a department from the guest WhatsApp list without deleting it.
                      </p>
                      {(flowSettings.main_menu_rows || []).map((row) => (
                        <div key={row.id} className="border border-gray-200 rounded-lg p-3 grid grid-cols-1 gap-3 bg-white">
                          <label className="flex items-center justify-between gap-3 cursor-pointer">
                            <span className="text-xs font-medium text-gray-700">Show in guest menu</span>
                            <input
                              type="checkbox"
                              checked={row.enabled !== false}
                              onChange={(e) => updateRowField('main_menu_rows', row.id, 'enabled', e.target.checked)}
                              className="h-4 w-4 text-gray-900 border-gray-300 rounded shrink-0"
                            />
                          </label>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
                            <input
                              type="text"
                              value={row.title || ''}
                              onChange={(e) => updateRowField('main_menu_rows', row.id, 'title', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                  </div>
                  </>
                  )}

                  {activeFlowSection === 'restaurants' && (
                  <>
                  <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                    <h3 className="text-sm font-semibold text-gray-900">PDF / Media Links</h3>
                    <p className="text-xs text-gray-600 mt-1">Control all WhatsApp document URLs, filenames and captions.</p>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Welcome Template Image URL (Optional)</label>
                      <input
                        type="url"
                        value={flowSettings.welcome_template_image_url || ''}
                        onChange={(e) => setFlowSettings((prev) => ({ ...prev, welcome_template_image_url: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="https://your-domain.com/oleander.png"
                      />
                    </div>

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Food Menu PDF URL</label>
                        <input
                          type="url"
                          value={flowSettings.food_menu_pdf_url || ''}
                          onChange={(e) => setFlowSettings((prev) => ({ ...prev, food_menu_pdf_url: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          placeholder="https://.../food-menu.pdf"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Food Menu PDF Filename</label>
                        <input
                          type="text"
                          value={flowSettings.food_menu_pdf_filename || ''}
                          onChange={(e) => setFlowSettings((prev) => ({ ...prev, food_menu_pdf_filename: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Food Menu Caption (optional)</label>
                      <input
                        type="text"
                        value={flowSettings.food_menu_pdf_caption || ''}
                        onChange={(e) => setFlowSettings((prev) => ({ ...prev, food_menu_pdf_caption: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>

                    <div className="mt-4 space-y-3">
                      <h4 className="text-sm font-semibold text-gray-900">Activity PDF Settings (Guest WhatsApp)</h4>
                      {ACTIVITY_PDF_OVERRIDE_KEYS.map((activityKey) => (
                        <div key={activityKey} className="border border-gray-200 rounded-lg p-3 grid grid-cols-1 md:grid-cols-3 gap-3 bg-white">
                          <div className="md:col-span-3 text-xs font-semibold text-gray-700">{activityKey}</div>
                          <input
                            type="url"
                            value={flowSettings.activity_pdf_overrides?.[activityKey]?.pdf_url || ''}
                            onChange={(e) => updateActivityPdfOverride(activityKey, 'pdf_url', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            placeholder="PDF URL (https://...pdf)"
                          />
                          <input
                            type="text"
                            value={flowSettings.activity_pdf_overrides?.[activityKey]?.filename || ''}
                            onChange={(e) => updateActivityPdfOverride(activityKey, 'filename', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            placeholder="Filename"
                          />
                          <input
                            type="text"
                            value={flowSettings.activity_pdf_overrides?.[activityKey]?.caption || ''}
                            onChange={(e) => updateActivityPdfOverride(activityKey, 'caption', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            placeholder="Caption"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                    <h3 className="text-sm font-semibold text-gray-900">Restaurant Booking Contact</h3>
                    <p className="text-xs text-gray-600 mt-1">
                      Guests now receive one common restaurant booking contact instead of a restaurant list.
                    </p>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Restaurant Description Message</label>
                      <textarea
                        value={flowSettings.restaurants_message || ''}
                        onChange={(e) => setFlowSettings((prev) => ({ ...prev, restaurants_message: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm min-h-[120px]"
                        placeholder={`*Restaurant Table Booking*\n\nOur restaurants:\n• Saltt Restaurant - Fine dining experience\n• Saltt Coffee House - Coffee, snacks, and quick bites\n• Common House - All-day casual dining\n\nFor reservations, please call the number below.`}
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        This text is sent first on WhatsApp when guest selects Restaurant Booking.
                      </p>
                    </div>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Contact Name</label>
                        <input
                          type="text"
                          value={flowSettings.restaurants_contact_name || ''}
                          onChange={(e) => setFlowSettings((prev) => ({ ...prev, restaurants_contact_name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          placeholder="Restaurant Reservations"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Contact Phone</label>
                        <input
                          type="text"
                          value={flowSettings.restaurants_contact_phone || ''}
                          onChange={(e) => setFlowSettings((prev) => ({ ...prev, restaurants_contact_phone: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          placeholder="+91 XXXXXXXXXX"
                        />
                      </div>
                    </div>
                  </div>
                  </>
                  )}

                  {activeFlowSection === 'recreation' && (
                    <div className="space-y-5">
                      <div className="border border-emerald-200 rounded-xl p-4 bg-emerald-50/70">
                        <h3 className="text-sm font-semibold text-emerald-900">🎾 Recreation Activities & Sports Booking</h3>
                        <p className="text-xs text-emerald-700 mt-1">
                          Configure the recreation activity PDF brochure, booking contact details, and the WhatsApp message sent when guests select Recreation Activities.
                        </p>
                      </div>

                      <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                        <h4 className="text-sm font-semibold text-gray-900">Recreation Booking Contact</h4>
                        <p className="text-xs text-gray-600 mt-1">
                          When guests select Recreation Activities in the WhatsApp menu, this contact card is sent directly to their chat so they can call or message to book.
                        </p>
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Recreation Contact Name</label>
                            <input
                              type="text"
                              value={flowSettings.recreation_contact_name || ''}
                              onChange={(e) => setFlowSettings((prev) => ({ ...prev, recreation_contact_name: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                              placeholder="Recreation Desk"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Recreation Contact Phone</label>
                            <input
                              type="text"
                              value={flowSettings.recreation_contact_phone || ''}
                              onChange={(e) => setFlowSettings((prev) => ({ ...prev, recreation_contact_phone: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white font-mono"
                              placeholder="+91 XXXXXXXXXX"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                        <h4 className="text-sm font-semibold text-gray-900">Recreation Brochure PDF (Sent on WhatsApp)</h4>
                        <p className="text-xs text-gray-600 mt-1">
                          The document sent to guests on WhatsApp containing padel, pickleball, trampoline and activity rules/timings.
                        </p>
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 bg-white p-3.5 border border-gray-200 rounded-lg">
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">PDF URL</label>
                            <input
                              type="url"
                              value={flowSettings.activity_pdf_overrides?.Recreation?.pdf_url || ''}
                              onChange={(e) => updateActivityPdfOverride('Recreation', 'pdf_url', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              placeholder="https://.../recreation_info.pdf"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Filename</label>
                            <input
                              type="text"
                              value={flowSettings.activity_pdf_overrides?.Recreation?.filename || ''}
                              onChange={(e) => updateActivityPdfOverride('Recreation', 'filename', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              placeholder="recreation_info.pdf"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Caption</label>
                            <input
                              type="text"
                              value={flowSettings.activity_pdf_overrides?.Recreation?.caption || ''}
                              onChange={(e) => updateActivityPdfOverride('Recreation', 'caption', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              placeholder="Recreation activities and guidelines"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                        <label className="block text-sm font-semibold text-gray-900 mb-1">WhatsApp Accompanying Message</label>
                        <p className="text-xs text-gray-500 mb-2">Message sent in chat along with the PDF and contact card.</p>
                        <textarea
                          value={flowSettings.recreation_message || ''}
                          onChange={(e) => setFlowSettings((prev) => ({ ...prev, recreation_message: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white min-h-[100px]"
                          placeholder="*Recreation Activities & Sports Booking*\n\nPlease find our recreation brochure above. Call or message the number below to book your padel, pickleball, trampoline or recreation slots."
                        />
                      </div>
                    </div>
                  )}

                  {activeFlowSection === 'common' && (
                  <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                    <h3 className="text-sm font-semibold text-gray-900">General Contact Numbers</h3>
                    <p className="text-xs text-gray-600 mt-1">Centralized numbers used in guest WhatsApp contact flows.</p>

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Front Office Contact Name</label>
                        <input
                          type="text"
                          value={flowSettings.front_office_contact_name || ''}
                          onChange={(e) => setFlowSettings((prev) => ({ ...prev, front_office_contact_name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Front Office Contact Phone</label>
                        <input
                          type="text"
                          value={flowSettings.front_office_contact_phone || ''}
                          onChange={(e) => setFlowSettings((prev) => ({ ...prev, front_office_contact_phone: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Recreation Contact Name</label>
                        <input
                          type="text"
                          value={flowSettings.recreation_contact_name || ''}
                          onChange={(e) => setFlowSettings((prev) => ({ ...prev, recreation_contact_name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Recreation Contact Phone</label>
                        <input
                          type="text"
                          value={flowSettings.recreation_contact_phone || ''}
                          onChange={(e) => setFlowSettings((prev) => ({ ...prev, recreation_contact_phone: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                  )}

                  {activeFlowSection === 'medical_emergency' && (
                    <div className="border border-red-200 rounded-xl p-4 bg-red-50">
                      <h3 className="text-sm font-semibold text-red-800">Medical Emergency</h3>
                      <p className="text-xs text-red-700 mt-1">
                        Medical emergency requests are raised as high priority and dispatched to Front Office staff plus HOD.
                      </p>
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Medical Emergency Contact Name</label>
                          <input
                            type="text"
                            value={flowSettings.medical_emergency_contact_name || ''}
                            onChange={(e) => setFlowSettings((prev) => ({ ...prev, medical_emergency_contact_name: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Medical Emergency Contact Phone</label>
                          <input
                            type="text"
                            value={flowSettings.medical_emergency_contact_phone || ''}
                            onChange={(e) => setFlowSettings((prev) => ({ ...prev, medical_emergency_contact_phone: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                            placeholder="+91 XXXXXXXXXX"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {activeFlowSection === 'security' && (
                    <div className="space-y-6">
                      <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                        <div className="flex justify-between items-center mb-4">
                          <div>
                            <h3 className="text-sm font-semibold text-gray-900">Security Assistance Options</h3>
                            <p className="text-xs text-gray-600 mt-1">Guests will choose from these options when they select Security assistance.</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => addRequestOptionRow('security_request_rows', 'security', 'Option')}
                            className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium hover:bg-gray-100"
                          >
                            Add Option
                          </button>
                        </div>
 
                        <div className="space-y-3">
                          {getSortedOptions(flowSettings.security_request_rows || []).map((row) => {
                            const isOthersRow = String(row.title || '').trim().toLowerCase() === 'others';
                            return (
                              <div key={row.id} className="border border-gray-200 rounded-lg p-3 bg-white space-y-2">
                                <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                                  <input
                                    type="text"
                                    value={row.title || ''}
                                    onChange={(e) => updateRequestOptionField('security_request_rows', row.id, 'title', e.target.value.slice(0, 20))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm md:col-span-2"
                                    placeholder="Option title (max 20 chars)"
                                  />
                                  <input
                                    type="text"
                                    value={row.description || ''}
                                    onChange={(e) => updateRequestOptionField('security_request_rows', row.id, 'description', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm md:col-span-2"
                                    placeholder="Short description"
                                  />
                                  <div className="flex flex-col gap-1">
                                    <label className="text-xs text-gray-500 leading-tight">Escalation (mins)<br/><span className="text-gray-400">0 = use rule default</span></label>
                                    <input
                                      type="number"
                                      min="0"
                                      value={row.escalation_minutes ?? 0}
                                      onChange={(e) => updateRequestOptionField('security_request_rows', row.id, 'escalation_minutes', e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0))}
                                      className="w-full px-2 py-2 border border-amber-300 rounded-lg text-sm bg-amber-50"
                                      placeholder="0"
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeRequestOptionRow('security_request_rows', row.id)}
                                    className="px-3 py-2 rounded-lg border border-red-300 text-red-600 text-xs font-medium hover:bg-red-50 self-end"
                                  >
                                    Remove
                                  </button>
                                </div>
                                {!isOthersRow && (
                                  <div className="pt-1.5 border-t border-gray-100 space-y-1.5">
                                    <label className="flex items-center gap-2 cursor-pointer select-none">
                                      <input
                                        type="checkbox"
                                        checked={Boolean(row.detail_prompt_enabled)}
                                        onChange={(e) => updateRequestOptionField('security_request_rows', row.id, 'detail_prompt_enabled', e.target.checked)}
                                        className="rounded border-gray-300 text-indigo-600"
                                        disabled={Boolean(row.auto_reply_enabled)}
                                      />
                                      <span className={`text-xs ${row.auto_reply_enabled ? 'text-gray-400' : 'text-gray-600'}`}>Ask guest for extra details before creating request</span>
                                    </label>
                                    {row.detail_prompt_enabled && !row.auto_reply_enabled && (
                                      <input
                                        type="text"
                                        value={row.detail_prompt_message || ''}
                                        onChange={(e) => updateRequestOptionField('security_request_rows', row.id, 'detail_prompt_message', e.target.value)}
                                        className="w-full px-3 py-1.5 border border-indigo-200 rounded-lg text-sm bg-indigo-50 placeholder-indigo-300"
                                        placeholder="Prompt message sent to guest (e.g. Please describe the incident)"
                                      />
                                    )}
                                    <label className="flex items-center gap-2 cursor-pointer select-none mt-2">
                                      <input
                                        type="checkbox"
                                        checked={Boolean(row.auto_reply_enabled)}
                                        onChange={(e) => {
                                          updateRequestOptionField('security_request_rows', row.id, 'auto_reply_enabled', e.target.checked);
                                          if (e.target.checked) updateRequestOptionField('security_request_rows', row.id, 'detail_prompt_enabled', false);
                                        }}
                                        className="rounded border-gray-300 text-emerald-600"
                                      />
                                      <span className="text-xs text-gray-600">Send direct auto-reply instead of creating a request</span>
                                    </label>
                                    {row.auto_reply_enabled && (
                                      <textarea
                                        value={row.auto_reply_message || ''}
                                        onChange={(e) => updateRequestOptionField('security_request_rows', row.id, 'auto_reply_message', e.target.value)}
                                        className="w-full px-3 py-1.5 border border-emerald-200 rounded-lg text-sm bg-emerald-50 placeholder-emerald-300 min-h-[60px]"
                                        placeholder="Auto-reply message (e.g. The Wi-Fi network is 'Guest' and password is 'Ely123')"
                                      />
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
 
                      <div className="border border-rose-200 rounded-xl p-4 bg-rose-50">
                        <h3 className="text-sm font-semibold text-rose-800">Security Contact Info</h3>
                        <p className="text-xs text-rose-700 mt-1">
                          This contact info will be sent to the guest as a backup after they submit any security request.
                        </p>
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Security Contact Name</label>
                            <input
                              type="text"
                              value={flowSettings.security_emergency_contact_name || ''}
                              onChange={(e) => setFlowSettings((prev) => ({ ...prev, security_emergency_contact_name: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Security Contact Phone</label>
                            <input
                              type="text"
                              value={flowSettings.security_emergency_contact_phone || ''}
                              onChange={(e) => setFlowSettings((prev) => ({ ...prev, security_emergency_contact_phone: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                              placeholder="+91 XXXXXXXXXX"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeFlowSection === 'housekeeping' && (
                    <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">Housekeeping Request Options</h3>
                          <p className="text-xs text-gray-600 mt-1">These options are shown in WhatsApp after guest selects Housekeeping Service.</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => addRequestOptionRow('housekeeping_request_rows', 'housekeeping', 'Option')}
                          className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium hover:bg-gray-100"
                        >
                          Add Option
                        </button>
                      </div>

                      {getSortedOptions(flowSettings.housekeeping_request_rows || []).map((row) => {
                        const isOthersRow = String(row.title || '').trim().toLowerCase() === 'others';
                        return (
                          <div key={row.id} className="border border-gray-200 rounded-lg p-3 bg-white space-y-2">
                            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                              <input
                                type="text"
                                value={row.title || ''}
                                onChange={(e) => updateRequestOptionField('housekeeping_request_rows', row.id, 'title', e.target.value.slice(0, 20))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm md:col-span-2"
                                placeholder="Option title (max 20 chars)"
                              />
                              <input
                                type="text"
                                value={row.description || ''}
                                onChange={(e) => updateRequestOptionField('housekeeping_request_rows', row.id, 'description', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm md:col-span-2"
                                placeholder="Short description"
                              />
                              <div className="flex flex-col gap-1">
                                <label className="text-xs text-gray-500 leading-tight">Escalation (mins)<br/><span className="text-gray-400">0 = use rule default</span></label>
                                <input
                                  type="number"
                                  min="0"
                                  value={row.escalation_minutes ?? 0}
                                  onChange={(e) => updateRequestOptionField('housekeeping_request_rows', row.id, 'escalation_minutes', e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0))}
                                  className="w-full px-2 py-2 border border-amber-300 rounded-lg text-sm bg-amber-50"
                                  placeholder="0"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => removeRequestOptionRow('housekeeping_request_rows', row.id)}
                                className="px-3 py-2 rounded-lg border border-red-300 text-red-600 text-xs font-medium hover:bg-red-50 self-end"
                              >
                                Remove
                              </button>
                            </div>
                            {!isOthersRow && (
                              <div className="pt-1.5 border-t border-gray-100 space-y-1.5">
                                <label className="flex items-center gap-2 cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(row.detail_prompt_enabled)}
                                    onChange={(e) => updateRequestOptionField('housekeeping_request_rows', row.id, 'detail_prompt_enabled', e.target.checked)}
                                    className="rounded border-gray-300 text-indigo-600"
                                    disabled={Boolean(row.auto_reply_enabled)}
                                  />
                                  <span className={`text-xs ${row.auto_reply_enabled ? 'text-gray-400' : 'text-gray-600'}`}>Ask guest for extra details before creating request</span>
                                </label>
                                {row.detail_prompt_enabled && !row.auto_reply_enabled && (
                                  <input
                                    type="text"
                                    value={row.detail_prompt_message || ''}
                                    onChange={(e) => updateRequestOptionField('housekeeping_request_rows', row.id, 'detail_prompt_message', e.target.value)}
                                    className="w-full px-3 py-1.5 border border-indigo-200 rounded-lg text-sm bg-indigo-50 placeholder-indigo-300"
                                    placeholder="Prompt message sent to guest (e.g. Please describe your request in detail)"
                                  />
                                )}
                                <label className="flex items-center gap-2 cursor-pointer select-none mt-2">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(row.auto_reply_enabled)}
                                    onChange={(e) => {
                                      updateRequestOptionField('housekeeping_request_rows', row.id, 'auto_reply_enabled', e.target.checked);
                                      if (e.target.checked) updateRequestOptionField('housekeeping_request_rows', row.id, 'detail_prompt_enabled', false);
                                    }}
                                    className="rounded border-gray-300 text-emerald-600"
                                  />
                                  <span className="text-xs text-gray-600">Send direct auto-reply instead of creating a request</span>
                                </label>
                                {row.auto_reply_enabled && (
                                  <textarea
                                    value={row.auto_reply_message || ''}
                                    onChange={(e) => updateRequestOptionField('housekeeping_request_rows', row.id, 'auto_reply_message', e.target.value)}
                                    className="w-full px-3 py-1.5 border border-emerald-200 rounded-lg text-sm bg-emerald-50 placeholder-emerald-300 min-h-[60px]"
                                    placeholder="Auto-reply message (e.g. The Wi-Fi network is 'Guest' and password is 'Ely123')"
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {activeFlowSection === 'it_support' && (
                    <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">IT Support Request Options</h3>
                          <p className="text-xs text-gray-600 mt-1">These options are shown in WhatsApp after guest selects IT Support.</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => addRequestOptionRow('it_support_request_rows', 'it_support', 'Option')}
                          className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium hover:bg-gray-100"
                        >
                          Add Option
                        </button>
                      </div>

                      {getSortedOptions(flowSettings.it_support_request_rows || []).map((row) => {
                        const isOthersRow = String(row.title || '').trim().toLowerCase() === 'others';
                        return (
                          <div key={row.id} className="border border-gray-200 rounded-lg p-3 bg-white space-y-2">
                            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                              <input
                                type="text"
                                value={row.title || ''}
                                onChange={(e) => updateRequestOptionField('it_support_request_rows', row.id, 'title', e.target.value.slice(0, 20))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm md:col-span-2"
                                placeholder="Option title (max 20 chars)"
                              />
                              <input
                                type="text"
                                value={row.description || ''}
                                onChange={(e) => updateRequestOptionField('it_support_request_rows', row.id, 'description', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm md:col-span-2"
                                placeholder="Short description"
                              />
                              <div className="flex flex-col gap-1">
                                <label className="text-xs text-gray-500 leading-tight">Escalation (mins)<br/><span className="text-gray-400">0 = use rule default</span></label>
                                <input
                                  type="number"
                                  min="0"
                                  value={row.escalation_minutes ?? 0}
                                  onChange={(e) => updateRequestOptionField('it_support_request_rows', row.id, 'escalation_minutes', e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0))}
                                  className="w-full px-2 py-2 border border-amber-300 rounded-lg text-sm bg-amber-50"
                                  placeholder="0"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => removeRequestOptionRow('it_support_request_rows', row.id)}
                                className="px-3 py-2 rounded-lg border border-red-300 text-red-600 text-xs font-medium hover:bg-red-50 self-end"
                              >
                                Remove
                              </button>
                            </div>
                            {!isOthersRow && (
                              <div className="pt-1.5 border-t border-gray-100 space-y-1.5">
                                <label className="flex items-center gap-2 cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(row.detail_prompt_enabled)}
                                    onChange={(e) => updateRequestOptionField('it_support_request_rows', row.id, 'detail_prompt_enabled', e.target.checked)}
                                    className="rounded border-gray-300 text-indigo-600"
                                    disabled={Boolean(row.auto_reply_enabled)}
                                  />
                                  <span className={`text-xs ${row.auto_reply_enabled ? 'text-gray-400' : 'text-gray-600'}`}>Ask guest for extra details before creating request</span>
                                </label>
                                {row.detail_prompt_enabled && !row.auto_reply_enabled && (
                                  <input
                                    type="text"
                                    value={row.detail_prompt_message || ''}
                                    onChange={(e) => updateRequestOptionField('it_support_request_rows', row.id, 'detail_prompt_message', e.target.value)}
                                    className="w-full px-3 py-1.5 border border-indigo-200 rounded-lg text-sm bg-indigo-50 placeholder-indigo-300"
                                    placeholder="Prompt message sent to guest (e.g. Please describe the issue in detail)"
                                  />
                                )}
                                <label className="flex items-center gap-2 cursor-pointer select-none mt-2">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(row.auto_reply_enabled)}
                                    onChange={(e) => {
                                      updateRequestOptionField('it_support_request_rows', row.id, 'auto_reply_enabled', e.target.checked);
                                      if (e.target.checked) updateRequestOptionField('it_support_request_rows', row.id, 'detail_prompt_enabled', false);
                                    }}
                                    className="rounded border-gray-300 text-emerald-600"
                                  />
                                  <span className="text-xs text-gray-600">Send direct auto-reply instead of creating a request</span>
                                </label>
                                {row.auto_reply_enabled && (
                                  <textarea
                                    value={row.auto_reply_message || ''}
                                    onChange={(e) => updateRequestOptionField('it_support_request_rows', row.id, 'auto_reply_message', e.target.value)}
                                    className="w-full px-3 py-1.5 border border-emerald-200 rounded-lg text-sm bg-emerald-50 placeholder-emerald-300 min-h-[60px]"
                                    placeholder="Auto-reply message (e.g. The Wi-Fi network is 'Guest' and password is 'Ely123')"
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {activeFlowSection === 'maintenance' && (
                    <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">Maintenance Request Options</h3>
                          <p className="text-xs text-gray-600 mt-1">These options are shown in WhatsApp after guest selects Maintenance Assistance.</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => addRequestOptionRow('maintenance_request_rows', 'maintenance', 'Option')}
                          className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium hover:bg-gray-100"
                        >
                          Add Option
                        </button>
                      </div>

                      {getSortedOptions(flowSettings.maintenance_request_rows || []).map((row) => {
                        const isOthersRow = String(row.title || '').trim().toLowerCase() === 'others';
                        return (
                          <div key={row.id} className="border border-gray-200 rounded-lg p-3 bg-white space-y-2">
                            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                              <input
                                type="text"
                                value={row.title || ''}
                                onChange={(e) => updateRequestOptionField('maintenance_request_rows', row.id, 'title', e.target.value.slice(0, 20))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm md:col-span-2"
                                placeholder="Option title (max 20 chars)"
                              />
                              <input
                                type="text"
                                value={row.description || ''}
                                onChange={(e) => updateRequestOptionField('maintenance_request_rows', row.id, 'description', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm md:col-span-2"
                                placeholder="Short description"
                              />
                              <div className="flex flex-col gap-1">
                                <label className="text-xs text-gray-500 leading-tight">Escalation (mins)<br/><span className="text-gray-400">0 = use rule default</span></label>
                                <input
                                  type="number"
                                  min="0"
                                  value={row.escalation_minutes ?? 0}
                                  onChange={(e) => updateRequestOptionField('maintenance_request_rows', row.id, 'escalation_minutes', e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0))}
                                  className="w-full px-2 py-2 border border-amber-300 rounded-lg text-sm bg-amber-50"
                                  placeholder="0"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => removeRequestOptionRow('maintenance_request_rows', row.id)}
                                className="px-3 py-2 rounded-lg border border-red-300 text-red-600 text-xs font-medium hover:bg-red-50 self-end"
                              >
                                Remove
                              </button>
                            </div>
                            {!isOthersRow && (
                              <div className="pt-1.5 border-t border-gray-100 space-y-1.5">
                                <label className="flex items-center gap-2 cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(row.detail_prompt_enabled)}
                                    onChange={(e) => updateRequestOptionField('maintenance_request_rows', row.id, 'detail_prompt_enabled', e.target.checked)}
                                    className="rounded border-gray-300 text-indigo-600"
                                    disabled={Boolean(row.auto_reply_enabled)}
                                  />
                                  <span className={`text-xs ${row.auto_reply_enabled ? 'text-gray-400' : 'text-gray-600'}`}>Ask guest for extra details before creating request</span>
                                </label>
                                {row.detail_prompt_enabled && !row.auto_reply_enabled && (
                                  <input
                                    type="text"
                                    value={row.detail_prompt_message || ''}
                                    onChange={(e) => updateRequestOptionField('maintenance_request_rows', row.id, 'detail_prompt_message', e.target.value)}
                                    className="w-full px-3 py-1.5 border border-indigo-200 rounded-lg text-sm bg-indigo-50 placeholder-indigo-300"
                                    placeholder="Prompt message sent to guest (e.g. Please describe the issue in detail)"
                                  />
                                )}
                                <label className="flex items-center gap-2 cursor-pointer select-none mt-2">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(row.auto_reply_enabled)}
                                    onChange={(e) => {
                                      updateRequestOptionField('maintenance_request_rows', row.id, 'auto_reply_enabled', e.target.checked);
                                      if (e.target.checked) updateRequestOptionField('maintenance_request_rows', row.id, 'detail_prompt_enabled', false);
                                    }}
                                    className="rounded border-gray-300 text-emerald-600"
                                  />
                                  <span className="text-xs text-gray-600">Send direct auto-reply instead of creating a request</span>
                                </label>
                                {row.auto_reply_enabled && (
                                  <textarea
                                    value={row.auto_reply_message || ''}
                                    onChange={(e) => updateRequestOptionField('maintenance_request_rows', row.id, 'auto_reply_message', e.target.value)}
                                    className="w-full px-3 py-1.5 border border-emerald-200 rounded-lg text-sm bg-emerald-50 placeholder-emerald-300 min-h-[60px]"
                                    placeholder="Auto-reply message (e.g. The Wi-Fi network is 'Guest' and password is 'Ely123')"
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {activeFlowSection === 'front_office' && (
                    <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">Front Office Request Options</h3>
                          <p className="text-xs text-gray-600 mt-1">These options are shown in WhatsApp after guest selects Front Office.</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => addRequestOptionRow('front_office_request_rows', 'front_office', 'Option')}
                          className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium hover:bg-gray-100"
                        >
                          Add Option
                        </button>
                      </div>

                      {getSortedOptions(flowSettings.front_office_request_rows || []).map((row) => {
                        const isOthersRow = String(row.title || '').trim().toLowerCase() === 'others';
                        return (
                          <div key={row.id} className="border border-gray-200 rounded-lg p-3 bg-white space-y-2">
                            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                              <input
                                type="text"
                                value={row.title || ''}
                                onChange={(e) => updateRequestOptionField('front_office_request_rows', row.id, 'title', e.target.value.slice(0, 20))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm md:col-span-2"
                                placeholder="Option title (max 20 chars)"
                              />
                              <input
                                type="text"
                                value={row.description || ''}
                                onChange={(e) => updateRequestOptionField('front_office_request_rows', row.id, 'description', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm md:col-span-2"
                                placeholder="Short description"
                              />
                              <div className="flex flex-col gap-1">
                                <label className="text-xs text-gray-500 leading-tight">Escalation (mins)<br/><span className="text-gray-400">0 = use rule default</span></label>
                                <input
                                  type="number"
                                  min="0"
                                  value={row.escalation_minutes ?? 0}
                                  onChange={(e) => updateRequestOptionField('front_office_request_rows', row.id, 'escalation_minutes', e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0))}
                                  className="w-full px-2 py-2 border border-amber-300 rounded-lg text-sm bg-amber-50"
                                  placeholder="0"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => removeRequestOptionRow('front_office_request_rows', row.id)}
                                className="px-3 py-2 rounded-lg border border-red-300 text-red-600 text-xs font-medium hover:bg-red-50 self-end"
                              >
                                Remove
                              </button>
                            </div>
                            {!isOthersRow && (
                              <div className="pt-1.5 border-t border-gray-100 space-y-1.5">
                                <label className="flex items-center gap-2 cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(row.detail_prompt_enabled)}
                                    onChange={(e) => updateRequestOptionField('front_office_request_rows', row.id, 'detail_prompt_enabled', e.target.checked)}
                                    className="rounded border-gray-300 text-indigo-600"
                                    disabled={Boolean(row.auto_reply_enabled)}
                                  />
                                  <span className={`text-xs ${row.auto_reply_enabled ? 'text-gray-400' : 'text-gray-600'}`}>Ask guest for extra details before creating request</span>
                                </label>
                                {row.detail_prompt_enabled && !row.auto_reply_enabled && (
                                  <input
                                    type="text"
                                    value={row.detail_prompt_message || ''}
                                    onChange={(e) => updateRequestOptionField('front_office_request_rows', row.id, 'detail_prompt_message', e.target.value)}
                                    className="w-full px-3 py-1.5 border border-indigo-200 rounded-lg text-sm bg-indigo-50 placeholder-indigo-300"
                                    placeholder="Prompt message sent to guest (e.g. Please enter your desired wake up time e.g. 7:00 AM)"
                                  />
                                )}
                                <label className="flex items-center gap-2 cursor-pointer select-none mt-2">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(row.auto_reply_enabled)}
                                    onChange={(e) => {
                                      updateRequestOptionField('front_office_request_rows', row.id, 'auto_reply_enabled', e.target.checked);
                                      if (e.target.checked) updateRequestOptionField('front_office_request_rows', row.id, 'detail_prompt_enabled', false);
                                    }}
                                    className="rounded border-gray-300 text-emerald-600"
                                  />
                                  <span className="text-xs text-gray-600">Send direct auto-reply instead of creating a request</span>
                                </label>
                                {row.auto_reply_enabled && (
                                  <textarea
                                    value={row.auto_reply_message || ''}
                                    onChange={(e) => updateRequestOptionField('front_office_request_rows', row.id, 'auto_reply_message', e.target.value)}
                                    className="w-full px-3 py-1.5 border border-emerald-200 rounded-lg text-sm bg-emerald-50 placeholder-emerald-300 min-h-[60px]"
                                    placeholder="Auto-reply message (e.g. The Wi-Fi network is 'Guest' and password is 'Ely123')"
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {activeFlowSection === 'transport' && (
                    <div className="space-y-6">
                      <div className="border border-sky-200 rounded-xl p-4 bg-sky-50">
                        <h3 className="text-sm font-semibold text-sky-800">🚗 Transport Arrangements</h3>
                        <p className="text-xs text-sky-700 mt-1">
                          When a guest selects Transport Arrangements, the system sends a brochure (if configured)
                          then prompts them for their requirements. Their details create a pending request in the
                          Transport Arrangements department.
                        </p>
                        <div className="mt-4 space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Transport Brochure PDF URL <span className="text-xs text-gray-500">(optional — leave blank to skip)</span></label>
                            <input
                              type="text"
                              value={flowSettings.transport_brochure_pdf_url || ''}
                              onChange={(e) => setFlowSettings((prev) => ({ ...prev, transport_brochure_pdf_url: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                              placeholder="https://your-cdn.com/transport-brochure.pdf"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Brochure / Intro Caption</label>
                            <textarea
                              value={flowSettings.transport_brochure_pdf_caption || ''}
                              onChange={(e) => setFlowSettings((prev) => ({ ...prev, transport_brochure_pdf_caption: e.target.value }))}
                              rows={3}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm"
                              placeholder="*🚗 Transport Arrangements*\n\nWe offer cab services, airport transfers, and more."
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Transport Contact Name</label>
                              <input
                                type="text"
                                value={flowSettings.transport_contact_name || ''}
                                onChange={(e) => setFlowSettings((prev) => ({ ...prev, transport_contact_name: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                                placeholder="Transport Desk"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Transport Contact Phone</label>
                              <input
                                type="text"
                                value={flowSettings.transport_contact_phone || ''}
                                onChange={(e) => setFlowSettings((prev) => ({ ...prev, transport_contact_phone: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                                placeholder="+91 XXXXXXXXXX"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeFlowSection === 'slot_capacities' && (
                    <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                      <h3 className="text-sm font-semibold text-gray-900">Slot Capacities</h3>
                      <p className="text-xs text-gray-600 mt-1 mb-4">
                        Set the maximum number of simultaneous bookings allowed per time slot for each service.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        {SLOT_SERVICES.map(service => (
                          <div key={service}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{service} Capacity</label>
                            <input
                              type="number"
                              min="1"
                              value={slotCapacities[service] || 1}
                              onChange={(e) => setSlotCapacities(prev => ({ ...prev, [service]: Math.max(1, parseInt(e.target.value, 10)) }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={savingFlow}
                      className="bg-gray-900 text-white px-5 py-2 rounded-lg hover:bg-gray-800 disabled:opacity-50"
                    >
                      {savingFlow ? 'Saving...' : 'Save WhatsApp Flow Settings'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </form>
        )}

        {canManageRouting && (isDispatchOnlyView || settingsTab === 'dispatch') && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Staff Dispatch Rules</h2>
                <p className="text-sm text-gray-600 mt-1">Forward requests by activity + room range. Staff receive a form button to submit their completion update. If no response is received within the configured minutes, the request escalates to the HOD.</p>
              </div>
              <button
                type="button"
                onClick={runEscalationCheck}
                className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
              >
                Run Escalation Check
              </button>
            </div>

            {routingMessage && (
              <div className={`rounded-lg px-3 py-2 text-sm ${routingMessage.includes('success') || routingMessage.includes('completed') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {routingMessage}
              </div>
            )}

            <form ref={routingFormRef} onSubmit={createRoutingRule} className={`border rounded-lg p-4 space-y-4 ${editingRuleId ? 'border-blue-300 bg-blue-50/30' : 'border-gray-200'}`}>
              {editingRuleId && (
                <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                  You are editing an existing rule. Update fields below and click <strong>Update Rule</strong>.
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Activity</label>
                  <select
                    value={newRule.department}
                    onChange={(e) => setNewRule((prev) => ({ ...prev, department: e.target.value, primary_staff_ids: [], hod_staff_ids: [], super_hod_staff_ids: [] }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    {displayActivities.map((activity) => (
                      <option key={activity} value={activity}>{activity}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Room Match Type</label>
                  <select
                    value={newRule.room_match_type}
                    onChange={(e) => setNewRule((prev) => ({ ...prev, room_match_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="range">Room Range</option>
                    <option value="custom">Custom Rooms</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">HOD Escalation (mins)</label>
                  <input
                    value={newRule.escalation_minutes}
                    onChange={(e) => setNewRule((prev) => ({ ...prev, escalation_minutes: e.target.value }))}
                    placeholder="10"
                    type="number"
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Super HOD Escalation (mins after HOD)</label>
                  <input
                    value={newRule.super_hod_escalation_minutes || 0}
                    onChange={(e) => setNewRule((prev) => ({ ...prev, super_hod_escalation_minutes: e.target.value }))}
                    placeholder="0 (disabled)"
                    type="number"
                    min="0"
                    className="w-full px-3 py-2 border border-purple-300 rounded-lg text-sm bg-purple-50"
                  />
                  <span className="text-[10px] text-gray-400">0 = disabled. Mins after HOD notified.</span>
                </div>
              </div>

              {newRule.room_match_type === 'range' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Room Start</label>
                    <input
                      value={newRule.room_start}
                      onChange={(e) => setNewRule((prev) => ({ ...prev, room_start: e.target.value }))}
                      placeholder="101"
                      type="number"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Room End</label>
                    <input
                      value={newRule.room_end}
                      onChange={(e) => setNewRule((prev) => ({ ...prev, room_end: e.target.value }))}
                      placeholder="150"
                      type="number"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Custom Rooms</label>
                  <input
                    value={newRule.room_custom_values}
                    onChange={(e) => setNewRule((prev) => ({ ...prev, room_custom_values: e.target.value }))}
                    placeholder="101, 103, A-12, 200-210"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">Use comma-separated room numbers, IDs, or numeric ranges.</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Primary Employees</label>
                  <div className="w-full border border-gray-300 rounded-lg bg-white p-2 max-h-[180px] overflow-auto space-y-2">
                    {getEmployeeOptions(newRule.department).length === 0 && (
                      <p className="text-xs text-gray-500">No employees found for this activity.</p>
                    )}
                    {getEmployeeOptions(newRule.department).map((staff) => {
                      const isSelected = Array.isArray(newRule.primary_staff_ids) && newRule.primary_staff_ids.includes(staff.id);
                      return (
                        <label key={staff.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => togglePrimaryStaffSelection(staff.id)}
                            className="h-4 w-4"
                          />
                          <span>{staff.name} ({staff.phone || 'No phone'})</span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Selected: {(newRule.primary_staff_ids || []).length}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">HODs (1st Escalation Tier)</label>
                  <div className="border border-gray-300 rounded-lg p-2 max-h-[180px] overflow-y-auto space-y-1 bg-white">
                    {getHodOptions(newRule.department).length === 0 ? (
                      <p className="text-xs text-gray-400 italic">No HODs found for this department</p>
                    ) : (
                      getHodOptions(newRule.department).map((staff) => {
                        const isSelected = Array.isArray(newRule.hod_staff_ids) && newRule.hod_staff_ids.includes(staff.id);
                        return (
                          <label key={staff.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 px-1 py-0.5 rounded">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleHodStaffSelection(staff.id)}
                              className="h-4 w-4"
                            />
                            <span>{staff.name} ({staff.phone || 'No phone'})</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Selected: {(newRule.hod_staff_ids || []).length}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-purple-700 mb-1 font-semibold">Super HODs (2nd Escalation Tier)</label>
                  <div className="border border-purple-200 rounded-lg p-2 max-h-[180px] overflow-y-auto space-y-1 bg-purple-50/50">
                    {getHodOptions(newRule.department).length === 0 ? (
                      <p className="text-xs text-gray-400 italic">No HODs found for this department</p>
                    ) : (
                      getHodOptions(newRule.department).map((staff) => {
                        const isSelected = Array.isArray(newRule.super_hod_staff_ids) && newRule.super_hod_staff_ids.includes(staff.id);
                        return (
                          <label key={staff.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-purple-100/50 px-1 py-0.5 rounded">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSuperHodStaffSelection(staff.id)}
                              className="h-4 w-4 text-purple-600"
                            />
                            <span>{staff.name} ({staff.phone || 'No phone'})</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                  <p className="text-xs text-purple-600 mt-1">
                    Selected: {(newRule.super_hod_staff_ids || []).length}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 justify-end">
                {editingRuleId && (
                  <button
                    type="button"
                    onClick={cancelEditRule}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm hover:bg-gray-800"
                >
                  {editingRuleId ? 'Update Rule' : 'Add Rule'}
                </button>
              </div>
            </form>

            {loadingRouting ? (
              <div className="text-sm text-gray-600">Loading routing rules...</div>
            ) : (
              <div className="overflow-auto border border-gray-200 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-700">
                    <tr>
                      <th className="text-left px-3 py-2">Activity</th>
                      <th className="text-left px-3 py-2">Room Match</th>
                      <th className="text-left px-3 py-2">Primary Employees</th>
                      <th className="text-left px-3 py-2">HOD Escalation</th>
                      <th className="text-left px-3 py-2">HOD (m)</th>
                      <th className="text-left px-3 py-2">Super HOD</th>
                      <th className="text-left px-3 py-2">Super HOD (+m)</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-left px-3 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {routingRules.map((rule) => (
                      <tr key={rule.id} className="border-t border-gray-200">
                        <td className="px-3 py-2">{rule.department}</td>
                        <td className="px-3 py-2">{getRoomMatchLabel(rule)}</td>
                        <td className="px-3 py-2">{Array.isArray(rule.primary_phones) && rule.primary_phones.length > 0 ? rule.primary_phones.join(', ') : rule.primary_phone}</td>
                        <td className="px-3 py-2">
                          {Array.isArray(rule.escalation_phones) && rule.escalation_phones.length > 0
                            ? rule.escalation_phones.join(', ')
                            : (rule.escalation_phone || '—')}
                        </td>
                        <td className="px-3 py-2">{rule.escalation_minutes}</td>
                        <td className="px-3 py-2">
                          {Array.isArray(rule.super_hod_phones) && rule.super_hod_phones.length > 0
                            ? rule.super_hod_phones.join(', ')
                            : (rule.super_hod_phone || '—')}
                        </td>
                        <td className="px-3 py-2">{rule.super_hod_escalation_minutes ? `+${rule.super_hod_escalation_minutes}m` : 'Disabled'}</td>
                        <td className="px-3 py-2">{rule.is_active ? 'Active' : 'Inactive'}</td>
                        <td className="px-3 py-2 flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => toggleRoutingRule(rule)}
                            className="px-2 py-1 rounded border border-gray-300 text-xs hover:bg-gray-50"
                          >
                            {rule.is_active ? 'Disable' : 'Enable'}
                          </button>
                          <button
                            type="button"
                            onClick={() => startEditRule(rule)}
                            disabled={editingRuleId === rule.id}
                            className="px-2 py-1 rounded border border-blue-300 text-blue-700 text-xs hover:bg-blue-50"
                          >
                            {editingRuleId === rule.id ? 'Editing...' : 'Edit'}
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteRoutingRule(rule.id)}
                            className="px-2 py-1 rounded border border-red-300 text-red-600 text-xs hover:bg-red-50"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                    {routingRules.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-3 py-5 text-center text-gray-500">No routing rules configured yet.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
              Dispatch progress is now visible directly in Service Requests (awaiting staff response, escalated, completed).
            </div>

            {(getEmployeeOptions(newRule.department).length === 0 || getHodOptions(newRule.department).length === 0) && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                Add Employee and HOD contacts for this activity in Department Staff before creating routing rules.
              </div>
            )}
          </div>
        )}

        {canManageFlow && (isAttendanceOnlyView || settingsTab === 'attendance') && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-5">
            {routingMessage && (
              <div className={`rounded-lg px-3 py-2 text-sm ${routingMessage.includes('success') || routingMessage.includes('completed') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {routingMessage}
              </div>
            )}

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Staff Attendance Insights</h3>
                <p className="text-xs text-gray-500 mt-1">Professional attendance summary with department and status filters.</p>
              </div>
              <input
                type="date"
                value={attendanceDate}
                onChange={(e) => {
                  setAttendanceDate(e.target.value);
                  fetchAttendance(e.target.value);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                <p className="text-xs text-gray-500">Total Staff</p>
                <p className="text-xl font-semibold text-gray-900">{attendanceInsights.totals.total_staff}</p>
              </div>
              <div className="border border-green-200 rounded-lg p-3 bg-green-50">
                <p className="text-xs text-green-700">Present</p>
                <p className="text-xl font-semibold text-green-800">{attendanceInsights.totals.present}</p>
              </div>
              <div className="border border-blue-200 rounded-lg p-3 bg-blue-50">
                <p className="text-xs text-blue-700">On Duty</p>
                <p className="text-xl font-semibold text-blue-800">{attendanceInsights.totals.on_duty}</p>
              </div>
              <div className="border border-indigo-200 rounded-lg p-3 bg-indigo-50">
                <p className="text-xs text-indigo-700">Checked Out</p>
                <p className="text-xl font-semibold text-indigo-800">{attendanceInsights.totals.checked_out}</p>
              </div>
              <div className="border border-red-200 rounded-lg p-3 bg-red-50">
                <p className="text-xs text-red-700">Absent</p>
                <p className="text-xl font-semibold text-red-800">{attendanceInsights.totals.absent}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <select
                value={attendanceDepartmentFilter}
                onChange={(e) => setAttendanceDepartmentFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="all">All Departments</option>
                {attendanceDepartmentOptions.map((department) => (
                  <option key={department} value={department}>{department}</option>
                ))}
              </select>

              <select
                value={attendanceStatusFilter}
                onChange={(e) => setAttendanceStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="all">All Status</option>
                <option value="on_duty">On Duty</option>
                <option value="checked_out">Checked Out</option>
                <option value="absent">Absent</option>
              </select>

              <input
                type="text"
                value={attendanceSearch}
                onChange={(e) => setAttendanceSearch(e.target.value)}
                placeholder="Search staff or phone"
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />

              <button
                type="button"
                onClick={() => {
                  setAttendanceDepartmentFilter('all');
                  setAttendanceStatusFilter('all');
                  setAttendanceSearch('');
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
              >
                Reset Filters
              </button>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="text-left px-3 py-2">Staff</th>
                    <th className="text-left px-3 py-2">Department</th>
                    <th className="text-left px-3 py-2">Phone</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Check-in Time</th>
                    <th className="text-left px-3 py-2">Check-out Time</th>
                    <th className="text-left px-3 py-2">Work Duration</th>
                    <th className="text-left px-3 py-2">Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAttendanceRecords.map((row) => (
                    <tr key={row.id} className="border-t border-gray-200">
                      <td className="px-3 py-2">{row.staff_name || 'Unknown Staff'}</td>
                      <td className="px-3 py-2">{row.staff_department || '—'}</td>
                      <td className="px-3 py-2">{row.phone}</td>
                      <td className="px-3 py-2">
                        {getAttendanceStatus(row) === 'on_duty' && (
                          <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">On Duty</span>
                        )}
                        {getAttendanceStatus(row) === 'checked_out' && (
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">Checked Out</span>
                        )}
                        {getAttendanceStatus(row) === 'absent' && (
                          <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">Absent</span>
                        )}
                      </td>
                      <td className="px-3 py-2">{formatAttendanceDateTime(row.checked_in_at)}</td>
                      <td className="px-3 py-2">{formatAttendanceDateTime(row.checked_out_at)}</td>
                      <td className="px-3 py-2">{getAttendanceDuration(row.checked_in_at, row.checked_out_at)}</td>
                      <td className="px-3 py-2">{row.last_event_type || '—'}</td>
                    </tr>
                  ))}
                  {filteredAttendanceRecords.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-3 py-5 text-center text-gray-500">No attendance records match selected filters.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Settings;
